import { CardData } from '../types/cardTypes';
import { AuthService } from './authService';
import { BanxaCacheService } from './banxaCacheService';
import { throwIfRateLimited } from './rateLimitedResponse';

// Banxa API Interfaces
export interface BanxaPrice {
  paymentMethodId: string;
  cryptoAmount: number;
  fiatAmount: number;
  processingFee: number;
  networkFee: number;
  coinCode: string;
  fiatCode: string;
  blockchain?: string;
}

export interface BanxaQuote {
  amount: number;
  prices: BanxaPrice[];
}

export interface BanxaPaymentMethod {
  id: string;
  name?: string;
  description?: string | null;
  supportedFiats?: string[];
  restrictedCountries?: string[];
}

export interface BanxaStoredPricesResponse {
  success: boolean;
  data: {
    quotes: BanxaQuote[];
    paymentMethods: BanxaPaymentMethod[];
  };
}

// Legacy alias for backward compatibility
export interface BanxaBulkPricesResponse extends BanxaStoredPricesResponse {}

export interface CachedBanxaPrices {
  data: BanxaBulkPricesResponse;
  timestamp: number;
  coinCode: string;
}

/**
 * API service for card-related operations
 * Replace the mock functions with actual API calls
 */
export class ApiService {
  private static baseUrl = process.env.REACT_APP_MCC_APP_BACKEND_API_URL || 'http://localhost:8080';

  /**
   * Fetch card data by card ID
   * Replace this with your actual API endpoint
   */
  static async getCardByCardId(cardId: string): Promise<CardData | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/card/${cardId}`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null; // Card not found
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const jsonResponse = await response.json();
      return jsonResponse.data; // Return the data property from the response
    } catch (error) {
      console.error('Error fetching card data:', error);
      throw error;
    }
  }

  /**
   * Fetch user information for a card ID
   */
  static async getUserForCard(cardId: string): Promise<{
    success: boolean;
    data: {
      cardId: string;
      user: {
        firstName: string | null;
        status: string;
      };
    };
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/card/${cardId}/user`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user for card:', error);
      throw error;
    }
  }

  

  /**
   * Handle email user (create or get existing user)
   */
  static async handleEmailUser(email: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/user/handle-email-user`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error handling email user:', error);
      throw error;
    }
  }

  /**
   * Send validation email for protected page access (reusable for any protected page)
   */
  static async sendValidationEmail(email: string, cardId: string, returnTo?: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const body: any = { email, cardId };
      if (returnTo) {
        body.returnTo = returnTo;
      }

      const response = await fetch(`${this.baseUrl}/api/public/user/send-validation-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error sending validation email:', error);
      throw error;
    }
  }

  /**
   * Validate email token for protected page access
   */
  static async validateEmailToken(token: string, cardId: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/user/validate-email?cardId=${cardId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error validating email token:', error);
      throw error;
    }
  }

  /**
   * Send verification email (for registration)
   */
  static async sendVerificationEmail(email: string, cardId: string, returnTo?: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const body: any = { email, cardId };
      if (returnTo) {
        body.returnTo = returnTo;
      }

      const response = await fetch(`${this.baseUrl}/api/public/user/send-verification-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  }

  /**
   * Verify email token
   */
  static async verifyEmailToken(token: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/user/verify-email-token`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error verifying email token:', error);
      throw error;
    }
  }

  /**
   * Validate email token for authentication only (no card linking logic)
   * Used for accessing protected pages
   */
  static async validateEmailTokenForAuth(token: string, cardId: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/user/validate-email-auth?cardId=${cardId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error validating email token for authentication:', error);
      throw error;
    }
  }

  /**
   * Get crypto rates
   */
  static async getCryptoRates(): Promise<{ [coin: string]: { buyValue: number; sellValue: number } }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/commercial/crypto-rates`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching crypto rates:', error);
      throw error;
    }
  }

  /**
   * Get all crypto products
   */
  static async getCryptoProducts(): Promise<Array<{
    id: string;
    name: string;
    symbol: string;
    purchaseOptions: number[];
    decimalPlaces: number;
    description?: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/commercial/products`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching crypto products:', error);
      throw error;
    }
  }

  /**
   * Get all crypto rates
   */
  static async getCryptoRatesList(): Promise<Array<{
    id: string;
    symbol: string;
    buyPrice: number;
    sellPrice: number;
    currency: string;
    lastUpdated: string;
    source?: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/commercial/rates`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching crypto rates list:', error);
      throw error;
    }
  }

  /**
   * Get card activation fee
   */
  static async getCardActivationFee(): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/commercial/card-activation-fee`);

      throwIfRateLimited(response);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data.fee;
    } catch (error) {
      console.error('Error fetching card activation fee:', error);
      // Return default fee if API fails
      return 7;
    }
  }

  /**
   * Calculate purchase details
   */
  static async calculatePurchase(symbol: string, amount: number): Promise<{
    product: {
      id: string;
      name: string;
      symbol: string;
      purchaseOptions: number[];
      decimalPlaces: number;
      description?: string;
    };
    rate: {
      id: string;
      symbol: string;
      buyPrice: number;
      sellPrice: number;
      currency: string;
      lastUpdated: string;
      source?: string;
    };
    amount: number;
    cryptoAmount: number;
    fee: number;
    totalCost: number;
  }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/commercial/calculate-purchase`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ symbol, amount }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('Error calculating purchase:', error);
      throw error;
    }
  }

  /**
   * Create Veriff session for KYC
   */
  static async createVeriffSession(cardId: string, mobileNumber: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/user/create-veriff-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cardId, mobileNumber }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating Veriff session:', error);
      throw error;
    }
  }

  /**
   * Add card to user (link existing card to verified user)
   */
  static async addCardToUser(cardId: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/user/add-card-to-user`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cardId }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error adding card to user:', error);
      throw error;
    }
  }

  /**
   * Add card to user by email (link existing card to verified user using email authentication)
   */
  static async addCardToUserByEmail(cardId: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/user/add-card-to-user-by-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cardId }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error adding card to user by email:', error);
      throw error;
    }
  }

  /**
   * Activate card with PIN
   */
  static async activateCard(cardId: string, userPin: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/activate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cardId, userPin }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error activating card:', error);
      throw error;
    }
  }

  /**
   * Generate a correlation ID for API requests
   */
  private static generateCorrelationId(): string {
    return `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Refresh JWT token silently
   */
  static async refreshToken(): Promise<{ success: boolean; message: string; data?: { token: string; user: any } }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/user/refresh`, {
        method: 'POST',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = 'https://www.mycryptocard.io/';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  

  // Order-related API methods
  static async createOrder(orderData: {
    cardId: string;
    cryptoType: string;
    amount: number;
    isGiftCard?: boolean;
  }): Promise<{
    id: string;
    cardId: string;
    cryptoType: string;
    amount: number;
    cryptoAmount: number;
    fee: number;
    totalCost: number;
    status: string;
    isGiftCard: boolean;
    createdAt: Date;
  }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(orderData),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data.order;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  static async getOrder(orderId: string): Promise<{
    id: string;
    cardId: string;
    cryptoType: string;
    amount: number;
    cryptoAmount: number;
    fee: number;
    totalCost: number;
    status: string;
    isGiftCard: boolean;
    createdAt: Date;
  }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/orders/${orderId}`, {
        method: 'GET',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data.order;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  static async completeOrder(orderId: string, paymentMethod: string): Promise<void> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/orders/${orderId}/complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ paymentMethod }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error completing order:', error);
      throw error;
    }
  }

  // Payment-related API methods
  static async createStripePaymentIntent(orderId: string): Promise<{ clientSecret: string }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
      };

      const response = await fetch(`${this.baseUrl}/api/public/orders/payment/stripe/create-intent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return { clientSecret: data.clientSecret };
    } catch (error) {
      console.error('Error creating Stripe payment intent:', error);
      throw error;
    }
  }


  /**
   * Setup biometric authentication for a user
   */
  static async setupBiometricAuth(
    cardId: string, 
    biometricData: any, 
    authentication: { signingMethodId: string; signingMethodValue: string }
  ): Promise<{ success: boolean; message?: string; error?: string; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/biometric`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cardId, biometricData, authentication }),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error setting up biometric auth:', error);
      throw error;
    }
  }

  /**
   * Get user's signing methods for authentication
   */
  static async getUserSigningMethods(cardId: string): Promise<{ success: boolean; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/${cardId}/signing-methods`, {
        method: 'GET',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting user signing methods:', error);
      throw error;
    }
  }

  /**
   * Get card's biometric methods
   */
  static async getCardBiometricMethods(cardId: string): Promise<{ success: boolean; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/biometric-methods?cardId=${cardId}`, {
        method: 'GET',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting user biometric methods:', error);
      throw error;
    }
  }

  /**
   * Remove a biometric device
   */
  static async removeBiometricDevice(
    cardId: string, 
    signingMethodId: string, 
    pin?: string, 
    biometricAuth?: { signingMethodId: string; signingMethodValue: string }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const requestBody: any = { cardId, signingMethodId };
      if (pin) {
        requestBody.pin = pin;
      }
      if (biometricAuth) {
        requestBody.biometricAuth = biometricAuth;
      }

      const response = await fetch(`${this.baseUrl}/api/card/biometric`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify(requestBody),
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error removing biometric device:', error);
      throw error;
    }
  }

  /**
   * Check card's biometric authentication status
   */
  static async checkBiometricStatus(cardId: string): Promise<{ success: boolean; data?: any }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/biometric-status?cardId=${cardId}`, {
        method: 'GET',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error checking biometric status:', error);
      throw error;
    }
  }

  /**
   * Get Banxa bulk prices for cryptocurrency with caching
   */
  static async getBanxaBulkPrices(fiatCode: string, coinCode: string, fiatAmounts: number[]): Promise<BanxaBulkPricesResponse> {
    try {
      // Check cache first
      const cachedData = BanxaCacheService.getCachedPrices(coinCode);
      if (cachedData) {
        return cachedData;
      }

      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const requestBody = {
        fiatCode,
        coinCode,
        fiatAmounts
      };

      const response = await fetch(`${this.baseUrl}/api/banxa-commercials/prices/bulk`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Cache the successful response
      BanxaCacheService.setCachedPrices(coinCode, data);
      
      return data;
    } catch (error) {
      console.error('Error getting Banxa bulk prices:', error);
      throw error;
    }
  }

  /**
   * Get stored prices from database (public endpoint)
   * Returns all stored prices for all coins
   */
  static async getStoredPrices(includeAllPaymentMethods: boolean = true): Promise<BanxaStoredPricesResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/prices/stored?includeAllPaymentMethods=${includeAllPaymentMethods}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting stored prices:', error);
      throw error;
    }
  }

  /**
   * Get stored prices from database with filters for order (protected endpoint)
   * Returns filtered prices by fiatCode, fiatAmounts, and coinCodes
   */
  static async getStoredPricesForOrder(
    fiatCode: string,
    fiatAmounts: number[],
    coinCodes: string[],
    includeAllPaymentMethods: boolean = true
  ): Promise<BanxaStoredPricesResponse> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const requestBody = {
        fiatCode,
        fiatAmounts,
        coinCodes,
        includeAllPaymentMethods
      };

      const response = await fetch(`${this.baseUrl}/api/banxa-commercials/prices/stored-for-order`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, clear auth and redirect
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting stored prices for order:', error);
      throw error;
    }
  }

  /**
   * Get stored prices for a card's cryptocurrency (public endpoint)
   * Returns prices for the card's coinSymbol with fiat=AUD
   */
  static async getCardPrices(
    cardId: string,
    includeAllPaymentMethods: boolean = true
  ): Promise<BanxaBulkPricesResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/public/card/${cardId}/prices?includeAllPaymentMethods=${includeAllPaymentMethods}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting card prices:', error);
      throw error;
    }
  }

  /**
   * Create a Banxa buy order from quote data
   */
  static async createQuoteOrder(quoteData: {
    cardId: string;
    fiatAmount: number;
    cryptoAmount: number;
    fiatCode: string;
    coinCode: string;
    paymentMethodId: string;
    processingFee: number;
    networkFee: number;
    blockchain?: string;
  }): Promise<{
    success: boolean;
    data?: {
      checkoutUrl: string;
      id: string;
      externalOrderId: string | null;
      externalCustomerId: string;
      fiat: string;
      fiatAmount: string;
      crypto: string;
      cryptoAmount: string | null;
      blockchain: string | null;
    };
    error?: string;
  }> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId()
      };

      const response = await fetch(`${this.baseUrl}/api/public/quote/create-order`, {
        method: 'POST',
        headers,
        body: JSON.stringify(quoteData)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating quote order:', error);
      throw error;
    }
  }

  /**
   * Estimated sellable balance for CardSell UI (static network fee reserve).
   * GET /api/card/:cardId/sellable-balance
   */
  static async getSellableBalance(cardId: string): Promise<{
    success: boolean;
    data?: {
      cardBalance: number;
      walletBalance: number;
      gasReserve: number;
      maxSellCrypto: number;
      chain: string;
      isEstimate?: boolean;
      estimationDisclaimer?: string;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader,
      };

      const response = await fetch(`${this.baseUrl}/api/card/${cardId}/sellable-balance`, {
        method: 'GET',
        headers,
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching sellable balance:', error);
      throw error;
    }
  }

  /**
   * Get card value with sell quotes for all payment methods
   * GET /api/card/:cardId/value
   */
  static async getCardValue(cardId: string): Promise<{
    success: boolean;
    data?: {
      quotes: Array<{
        paymentMethodId: string;
        fiatCode: string;
        fiatAmount: number;
        processingFee: number;
        networkFee: number;
        paymentMethodName?: string;
      }>;
      paymentMethods: Array<{
        id: string;
        name: string;
      }>;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/${cardId}/value`, {
        method: 'GET',
        headers
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting card value:', error);
      throw error;
    }
  }

  /**
   * Get Banxa order status URL for the card's latest order (protected).
   * GET /api/card/:cardId/banxa-order-status
   */
  static async getBanxaOrderStatusUrl(cardId: string): Promise<{
    success: boolean;
    data?: { banxaStatusUrl: string | null };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/${cardId}/banxa-order-status`, {
        method: 'GET',
        headers
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting Banxa order status URL:', error);
      throw error;
    }
  }

  /**
   * Create a sell order (Banxa off-ramp)
   * POST /api/card/create-sell-order
   * Note: crypto and blockchain are retrieved from card details on the backend
   */
  static async createSellOrder(request: {
    cardId: string;
    cryptoAmount: number;
    fiatAmount: number;
    fiat: string;
    paymentMethodId: string;
    processingFee: number;
    networkFee: number;
  }): Promise<{
    success: boolean;
    data?: {
      checkoutUrl: string;
      orderId: string;
      banxaOrderId: string;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/create-sell-order`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating sell order:', error);
      throw error;
    }
  }

  /**
   * Webhook-first sell: server-trusted readiness (authenticated).
   * GET /api/card/:cardId/sell-transfer-ready/:orderId
   */
  static async getSellTransferReady(
    cardId: string,
    internalOrderId: string
  ): Promise<{
    success: boolean;
    data?: {
      ready: boolean;
      internalOrderId: string;
      banxaOrderId: string | null;
      status: string;
      coinType: string | null;
      coinAmount: string | null;
      blockchain: string | null;
      fiat: string | null;
      fiatAmount: string | null;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(
        `${this.baseUrl}/api/card/${encodeURIComponent(cardId)}/sell-transfer-ready/${encodeURIComponent(internalOrderId)}`,
        { method: 'GET', headers }
      );

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error getting sell transfer ready state:', error);
      throw error;
    }
  }

  /**
   * Validate sell order from Banxa redirect
   * POST /api/card/validate-sell-order
   */
  static async validateSellOrder(request: {
    cardId: string;
    orderId: string;
    providerWalletAddress: string;
    blockchain: string;
    coinType: string;
    coinAmount: string;
    customerWalletAddress?: string;
  }): Promise<{
    success: boolean;
    data?: {
      orderId: string;
      status: string;
    };
    error?: string;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId()
      };

      const response = await fetch(`${this.baseUrl}/api/card/validate-sell-order`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error validating sell order:', error);
      throw error;
    }
  }

  /**
   * Build Venly transfer after sell order validation (before PIN).
   * POST /api/card/:cardId/prepare-sell-transfer
   */
  static async prepareSellTransfer(request: {
    cardId: string;
    orderId: string;
  }): Promise<{
    success: boolean;
    data?: {
      orderId: string;
      preparedCryptoAmount: number;
      feeReserveRaw: number;
      chain: string;
      venlyPreparedAt: string;
      estimationNote: string;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader,
      };

      const response = await fetch(
        `${this.baseUrl}/api/card/${request.cardId}/prepare-sell-transfer`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ orderId: request.orderId }),
        }
      );

      throwIfRateLimited(response);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error preparing sell transfer:', error);
      throw error;
    }
  }

  /**
   * Execute prepared Venly transfer with PIN.
   * POST /api/card/transfer-coin
   */
  static async transferCoin(request: {
    cardId: string;
    orderId: string;
    pin: string;
    signingMethodId: string;
  }): Promise<{
    success: boolean;
    data?: {
      transactionHash?: string;
      status: string;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': this.generateCorrelationId(),
        ...authHeader
      };

      const response = await fetch(`${this.baseUrl}/api/card/transfer-coin`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });

      throwIfRateLimited(response);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          window.location.href = '/new-customer';
          throw new Error('Authentication required');
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error transferring coin:', error);
      throw error;
    }
  }

  static async getSumsubAccessToken(cardId: string): Promise<{
    success: boolean;
    data?: { token: string; userId: string };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const q = new URLSearchParams({ cardId });
      const response = await fetch(`${this.baseUrl}/api/user/sumsub/access-token?${q.toString()}`, {
        method: 'GET',
        headers: {
          ...authHeader,
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/new-customer?redirect=${redirect}`;
          return { success: false, error: 'Authentication required' };
        }
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (error) {
      console.error('Error fetching Sumsub access token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static async getSumsubKycStatus(): Promise<{
    success: boolean;
    data?: {
      sumsubKycStatus: string;
      sumsubReviewAnswer: string | null;
      sumsubReviewRejectType?: string | null;
      sumsubRejectLabels?: string[];
      moderationComment?: string | null;
      banxaIdentityRegistered: boolean;
      banxaError: boolean;
      readyForActivation: boolean;
    };
    error?: string;
  }> {
    try {
      const authHeader = AuthService.getAuthHeader();
      const response = await fetch(`${this.baseUrl}/api/user/sumsub/kyc-status`, {
        method: 'GET',
        headers: {
          ...authHeader,
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          AuthService.logout();
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/new-customer?redirect=${redirect}`;
          return { success: false, error: 'Authentication required' };
        }
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (error) {
      console.error('Error fetching Sumsub KYC status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static async getPublicScannerTestSumsubAccessToken(
    cardId?: string,
    externalUserId?: string,
    email?: string
  ): Promise<{
    success: boolean;
    data?: { token: string; userId: string };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams();
      if (cardId?.trim()) q.set('cardId', cardId.trim());
      if (externalUserId?.trim()) q.set('externalUserId', externalUserId.trim());
      if (email?.trim()) q.set('email', email.trim());
      const query = q.toString();
      const response = await fetch(`${this.baseUrl}/api/public/sumsub/scanner-test/access-token${query ? `?${query}` : ''}`, {
        method: 'GET',
        headers: {
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (error) {
      console.error('Error fetching public scanner-test Sumsub access token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static async getPublicScannerTestSumsubKycStatus(cardId?: string): Promise<{
    success: boolean;
    data?: {
      sumsubKycStatus: string;
      sumsubReviewAnswer: string | null;
      moderationComment?: string | null;
      banxaIdentityRegistered: boolean;
      banxaError: boolean;
      readyForActivation: boolean;
    };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams();
      if (cardId?.trim()) q.set('cardId', cardId.trim());
      const query = q.toString();
      const response = await fetch(`${this.baseUrl}/api/public/sumsub/scanner-test/kyc-status${query ? `?${query}` : ''}`, {
        method: 'GET',
        headers: {
          'x-correlation-id': this.generateCorrelationId()
        }
      });

      throwIfRateLimited(response);
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (error) {
      console.error('Error fetching public scanner-test Sumsub KYC status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Scanner-test: full applicant + base64 document bytes + optional DI addresses (public route; PII).
   */
  static async getPublicScannerTestApplicantPreview(externalUserId: string): Promise<{
    success: boolean;
    data?: {
      externalUserId: string;
      applicantId: string;
      inspectionId: string | null;
      names: { firstName?: string; lastName?: string } | null;
      dateOfBirth: string | null;
      documentIntelligenceConfigured: boolean;
      applicant: unknown;
      metadataResources: unknown;
      images: Array<{
        imageId: string;
        fileMetadata?: unknown;
        idDocDef?: unknown;
        contentType?: string;
        dataBase64?: string;
        diSkippedReason?: string;
        extractedAddress?: {
          addressLine1?: string;
          city?: string;
          state?: string;
          postcode?: string;
          country?: string;
        } | null;
        fetchError?: string;
      }>;
      hasDocumentResources: boolean;
    };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams();
      q.set('externalUserId', externalUserId.trim());
      const response = await fetch(
        `${this.baseUrl}/api/public/sumsub/scanner-test/applicant-preview?${q.toString()}`,
        {
          method: 'GET',
          headers: {
            'x-correlation-id': this.generateCorrelationId()
          }
        }
      );

      throwIfRateLimited(response);
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (error) {
      console.error('Error fetching scanner-test applicant preview:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
} 